import { Observation } from "@medplum/fhirtypes";

import { Extensions } from "@/fhir/enums/extensions";
import { LOINCCodes } from "@/fhir/enums/loinc";
import { LaborFlowsheetForms, QuiltedHealthCodes } from "@/fhir/enums/quilted-health";
import { compareDateTimeStrings } from "@/utils/datetime";

export interface RefCell<T> {
  current: T;
}

/**
 * Merges websocket observation updates with the latest Redux entities and the hook's
 * cache so `loadPostpartumObservations` (setAll) never replaces the full set when this
 * instance's cache was empty (e.g. second tab mounted later than the first).
 */
export function mergeIncomingObservationsIntoCache(
  incomingObservations: Observation[],
  reduxEntities: Record<string, Observation | undefined>,
  cacheRef: RefCell<Map<string, Observation>>,
): Observation[] {
  const mergedObservationsById = new Map<string, Observation>();
  for (const entityObservation of Object.values(reduxEntities)) {
    if (entityObservation?.id) {
      mergedObservationsById.set(entityObservation.id, entityObservation);
    }
  }
  cacheRef.current.forEach((cachedObservation, observationId) => {
    mergedObservationsById.set(observationId, cachedObservation);
  });
  incomingObservations.forEach((incomingObservation) => {
    if (incomingObservation.id) {
      mergedObservationsById.set(incomingObservation.id, incomingObservation);
    }
  });
  cacheRef.current = mergedObservationsById;
  return Array.from(mergedObservationsById.values());
}

/**
 * Same filtering as `usePostpartumObservations` process step, without dispatching.
 */
export function filterObservationsForPostpartumRedux(
  observations: Observation[],
  birthDateTime: string | undefined,
): Observation[] {
  const dateFilteredObservations = observations.filter((observation) => {
    if (!observation.effectiveDateTime || !birthDateTime) return true;

    const isBirthingObservation = observation.code?.coding?.some(
      (coding) => coding.code === LaborFlowsheetForms.Birthing,
    );

    if (isBirthingObservation) return true;

    const isBirthDateTimeObservation = observation.code?.coding?.some(
      (coding) => coding.code === QuiltedHealthCodes.BirthDateTime,
    );

    if (isBirthDateTimeObservation) return true;

    const isCumulativeBloodLossObservation = observation.code?.coding?.some(
      (coding) => coding.code === LOINCCodes.CumulativeBloodLoss,
    );

    if (isCumulativeBloodLossObservation) return true;

    return compareDateTimeStrings(observation.effectiveDateTime, birthDateTime);
  });

  const editedByPreviousVersionReference = new Map<string, Observation>();

  dateFilteredObservations.forEach((observation) => {
    if (observation.status !== "final") return;
    observation.extension?.forEach((extension) => {
      if (extension.url !== Extensions.ObservationPreviousVersion) return;
      const previousVersionReference = extension.valueReference?.reference;
      if (!previousVersionReference) return;
      editedByPreviousVersionReference.set(previousVersionReference, observation);
    });
  });

  const cancelledToEdited = new Map<string, Observation>();
  dateFilteredObservations.forEach((observation) => {
    if (observation.status !== "cancelled" || !observation.id) return;
    const editedVersion = editedByPreviousVersionReference.get(`Observation/${observation.id}`);
    if (editedVersion) {
      cancelledToEdited.set(observation.id, editedVersion);
    }
  });

  return dateFilteredObservations.filter((observation) => {
    if (observation.status === "final") return true;
    if (
      observation.status === "cancelled" &&
      (!observation.id || !cancelledToEdited.has(observation.id))
    ) {
      return true;
    }
    return false;
  });
}
